import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  MapPin, BarChart3, FileText, Bell, Home, Map, LayoutDashboard,
  Plus, List, User, LogIn, CheckCircle, Users, Clock, TrendingUp,
  Eye, UserCheck, ChevronRight
} from 'lucide-react';

const navLinks = [
  { path: '/', label: 'Inicio', icon: Home },
  { path: '/mapa', label: 'Mapa', icon: Map },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/nueva-gestion', label: 'Nueva Gestion', icon: FileText },
  { path: '/mis-gestiones', label: 'Mis Gestiones', icon: Bell },
];

const stats = [
  { value: '5,234', label: 'Gestiones Resueltas', icon: CheckCircle },
  { value: '45,890', label: 'Ciudadanos Beneficiados', icon: Users },
  { value: '12', label: 'Dias Promedio', icon: Clock },
  { value: '94%', label: 'Satisfaccion', icon: TrendingUp },
];

const features = [
  { title: 'Mapa Interactivo', desc: 'Visualiza todas las gestiones en tiempo real con ubicacion precisa', icon: MapPin },
  { title: 'Transparencia Total', desc: 'Conoce el estado de cada gestion y el impacto en tu comunidad', icon: Eye },
  { title: 'Seguimiento en Tiempo Real', desc: 'Recibe notificaciones sobre el avance de las gestiones', icon: Clock },
  { title: 'Analisis y Metricas', desc: 'Dashboard con estadisticas detalladas por zona y tipo de gestion', icon: BarChart3 },
  { title: 'Colaboracion Ciudadana', desc: 'Brigadistas en campo registrando necesidades de la comunidad', icon: UserCheck },
  { title: 'Resultados Medibles', desc: 'Cuantifica el impacto: beneficiarios, ahorros y mejoras', icon: CheckCircle },
];

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#E4E4E7]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Ciudad Inteligente" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-lg font-bold text-[#0052FF]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Ciudad Inteligente
            </span>
          </Link>

          {/* Center Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="landing-nav">
            {navLinks.map(item => {
              const Icon = item.icon;
              const isHome = item.path === '/';
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`landing-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isHome
                      ? 'bg-[#0052FF] text-white'
                      : 'text-[#52525B] hover:bg-[#10B981] hover:text-white'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User / Login */}
          {user ? (
            <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#52525B] hover:text-[#0A0A0A] transition-colors">
              <div className="w-8 h-8 rounded-full bg-[#0052FF]/10 flex items-center justify-center">
                <User className="w-4 h-4 text-[#0052FF]" />
              </div>
              <span className="hidden sm:inline font-medium">{user.name}</span>
            </Link>
          ) : (
            <Link to="/login" data-testid="landing-login-btn">
              <Button variant="ghost" className="flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#0A0A0A]">
                <div className="w-8 h-8 rounded-full bg-[#0052FF]/10 flex items-center justify-center">
                  <LogIn className="w-4 h-4 text-[#0052FF]" />
                </div>
                <span className="hidden sm:inline">Iniciar sesion</span>
              </Button>
            </Link>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#e8f5e9]/60 via-[#e8f5e9]/30 to-[#FAFAFA]" />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-36 text-center">
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter bg-gradient-to-r from-[#0052FF] to-[#0f8a6e] bg-clip-text text-transparent leading-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Ciudad Inteligente
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#52525B] max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Transparencia y eficiencia en la gestion urbana. Conectamos ciudadanos
            con gobiernos locales para una ciudad mejor.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/mapa">
              <Button
                data-testid="hero-map-btn"
                className="h-12 px-8 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold text-sm shadow-lg shadow-[#0052FF]/20 transition-all duration-200"
              >
                <MapPin className="w-4 h-4 mr-2" /> Ver Mapa de Gestiones
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                data-testid="hero-dashboard-btn"
                variant="outline"
                className="h-12 px-8 rounded-lg border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] text-[#0A0A0A] font-bold text-sm shadow-sm transition-all duration-200"
              >
                <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 -mt-4 mb-16" data-testid="stats-section">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} data-testid={`landing-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white border border-[#E4E4E7] rounded-xl hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 sm:p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-[#0052FF]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#0052FF]" />
                  </div>
                  <p className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tighter" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {s.value}
                  </p>
                  <p className="text-sm text-[#52525B] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {s.label}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16" data-testid="features-section">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            ¿Como Funciona?
          </h2>
          <p className="mt-3 text-base text-[#52525B]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Una plataforma completa para gestion urbana transparente
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <Card key={f.title} data-testid={`feature-card-${f.title.toLowerCase().replace(/\s/g, '-')}`} className="bg-white border border-[#E4E4E7] rounded-xl hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 sm:p-8">
                  <div className="w-12 h-12 rounded-xl bg-[#0052FF]/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#0052FF]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {f.title}
                  </h3>
                  <p className="text-sm text-[#52525B] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {f.desc}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="mx-4 sm:mx-6 lg:mx-auto max-w-[1200px] mb-16" data-testid="cta-section">
        <div className="rounded-2xl bg-gradient-to-r from-[#0052FF] to-[#0f8a6e] px-6 sm:px-12 py-16 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Unete a la Transformacion Urbana
          </h2>
          <p className="mt-4 text-base sm:text-lg text-white/80 max-w-xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Comienza a monitorear y participar en la mejora de tu comunidad
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/nueva-gestion">
              <Button
                data-testid="cta-register-btn"
                className="h-12 px-8 rounded-lg bg-white text-[#0A0A0A] hover:bg-white/90 font-bold text-sm shadow-lg transition-all duration-200"
              >
                <FileText className="w-4 h-4 mr-2" /> Registrar Gestion
              </Button>
            </Link>
            <Link to="/mis-gestiones">
              <Button
                data-testid="cta-gestiones-btn"
                variant="outline"
                className="h-12 px-8 rounded-lg border-white/30 bg-white/10 hover:bg-white/20 text-white font-bold text-sm backdrop-blur-sm transition-all duration-200"
              >
                <Bell className="w-4 h-4 mr-2" /> Mis Gestiones
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E4E4E7] bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Ciudad Inteligente" className="w-7 h-7 rounded-md object-contain" />
            <span className="text-sm font-bold text-[#0052FF]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Ciudad Inteligente
            </span>
          </div>
          <p className="text-xs text-[#52525B]">Transparencia y eficiencia en la gestion urbana</p>
        </div>
      </footer>
    </div>
  );
}
